import { getDate } from '@einhaus/meteoric-helpers';
import {
    type BarDuration,
    type ChartPatternAuditArtifact,
    type ChartPatternAuditItemDetail,
    type ChartPatternAuditReasonCode,
    type ChartPatternAuditRenderStatus,
    type ChartPatternAuditVerdict,
    type ChartPatternKey as ChartPatternKeyType,
    type ChartPatternState as ChartPatternStateType,
    ChartPatternAuditArtifactType,
    ChartPatternAuditReasonCode as ChartPatternAuditReasonCodeEnum,
    ChartPatternAuditRenderStatus as ChartPatternAuditRenderStatusEnum,
    ChartPatternAuditVerdict as ChartPatternAuditVerdictEnum,
    ChartPatternKey,
    ChartPatternState
} from '@einhaus/project314-types';
import { API_DOMAIN } from '@/api/trpc';

// ── Form state types ──

export type CreateBatchFormState = {
    name: string;
    patternKey: ChartPatternKeyType | '';
    barDuration: BarDuration | '';
    maxItems: string;
    minQualityScore: string;
    state: ChartPatternStateType | '';
    onlySelectedOccurrences: boolean;
    notes: string;
};

export type PrepareSessionFormState = {
    barDuration: BarDuration;
    historicalLookbackDays: string;
    historicalMinQualityScore: string;
    includeHistoricalBatches: boolean;
    maxItemsPerBatch: string;
    patternKey: ChartPatternKeyType;
};

export type ReviewFormState = {
    verdict: ChartPatternAuditVerdict | '';
    confidence: string;
    reasonCodes: ChartPatternAuditReasonCode[];
    notes: string;
};

// ── Option arrays ──

export const patternOptions: { value: ChartPatternKeyType | ''; label: string }[] = [
    { value: '', label: 'All patterns' },
    { value: ChartPatternKey.cupHandle, label: 'Cup & Handle' },
    { value: ChartPatternKey.doubleBottom, label: 'Double Bottom' },
    { value: ChartPatternKey.doubleTop, label: 'Double Top' },
    { value: ChartPatternKey.ascendingTriangle, label: 'Ascending Triangle' },
    { value: ChartPatternKey.descendingTriangle, label: 'Descending Triangle' },
    { value: ChartPatternKey.symmetricalTriangle, label: 'Symmetrical Triangle' },
    { value: ChartPatternKey.bullFlag, label: 'Bull Flag' },
    { value: ChartPatternKey.bearFlag, label: 'Bear Flag' },
    { value: ChartPatternKey.headShoulders, label: 'Head & Shoulders' },
    { value: ChartPatternKey.inverseHeadShoulders, label: 'Inverse Head & Shoulders' },
    { value: ChartPatternKey.risingWedge, label: 'Rising Wedge' },
    { value: ChartPatternKey.fallingWedge, label: 'Falling Wedge' }
];

export const timframeOptions: { value: BarDuration | ''; label: string }[] = [
    { value: '', label: 'All timeframes' },
    { value: '5m', label: '5 Minute' },
    { value: '15m', label: '15 Minute' },
    { value: '30m', label: '30 Minute' },
    { value: '240m', label: '4 Hour' },
    { value: '180m', label: '3 Hour' },
    { value: '120m', label: '2 Hour' },
    { value: '60m', label: '60 Minute' },
    { value: '1d', label: 'Daily' },
    { value: '1w', label: 'Weekly' }
];

export const statusOptions: { value: ChartPatternStateType | ''; label: string }[] = [
    { value: '', label: 'All statuses' },
    { value: ChartPatternState.forming, label: 'Forming' },
    { value: ChartPatternState.formed, label: 'Formed' },
    { value: ChartPatternState.triggered, label: 'Triggered' },
    { value: ChartPatternState.successful, label: 'Successful' },
    { value: ChartPatternState.failed, label: 'Failed' },
    { value: ChartPatternState.expired, label: 'Expired' }
];

export const verdictOptions: { value: ChartPatternAuditVerdict; label: string; shortLabel: string; color: string }[] = [
    { value: ChartPatternAuditVerdictEnum.correct, label: 'Valid', shortLabel: 'Valid', color: 'green' },
    { value: ChartPatternAuditVerdictEnum.weakButAcceptable, label: 'Borderline', shortLabel: 'Bord.', color: 'yellow' },
    { value: ChartPatternAuditVerdictEnum.incorrect, label: 'Invalid', shortLabel: 'Invalid', color: 'red' },
    { value: ChartPatternAuditVerdictEnum.duplicate, label: 'Duplicate', shortLabel: 'Dup.', color: 'orange' },
    { value: ChartPatternAuditVerdictEnum.uncertain, label: 'Skip', shortLabel: 'Skip', color: 'gray' }
];

export const issueLabels: Record<ChartPatternAuditReasonCode, string> = {
    [ChartPatternAuditReasonCodeEnum.notRounded]: 'Not rounded',
    [ChartPatternAuditReasonCodeEnum.tooVShaped]: 'Too V-shaped',
    [ChartPatternAuditReasonCodeEnum.handleTooDeep]: 'Handle too deep',
    [ChartPatternAuditReasonCodeEnum.handleTooLong]: 'Handle too long',
    [ChartPatternAuditReasonCodeEnum.handleTooLow]: 'Handle too low',
    [ChartPatternAuditReasonCodeEnum.badRimSymmetry]: 'Bad rim symmetry',
    [ChartPatternAuditReasonCodeEnum.weakImpulseLeg]: 'Weak impulse leg',
    [ChartPatternAuditReasonCodeEnum.poleTooLong]: 'Pole too long',
    [ChartPatternAuditReasonCodeEnum.flagTooFlat]: 'Flag too flat',
    [ChartPatternAuditReasonCodeEnum.flagTooDeep]: 'Flag too deep',
    [ChartPatternAuditReasonCodeEnum.flagTooWide]: 'Flag too wide',
    [ChartPatternAuditReasonCodeEnum.poorChannelStructure]: 'Poor channel structure',
    [ChartPatternAuditReasonCodeEnum.poorTriangleStructure]: 'Poor triangle structure',
    [ChartPatternAuditReasonCodeEnum.weakPriorTrend]: 'Weak prior trend',
    [ChartPatternAuditReasonCodeEnum.upperBoundaryWrongSlope]: 'Upper boundary wrong slope',
    [ChartPatternAuditReasonCodeEnum.lowerBoundaryWrongSlope]: 'Lower boundary wrong slope',
    [ChartPatternAuditReasonCodeEnum.weakBoundaryTouches]: 'Weak boundary touches',
    [ChartPatternAuditReasonCodeEnum.poorCompression]: 'Poor compression',
    [ChartPatternAuditReasonCodeEnum.poorTrendlineFit]: 'Poor trendline fit',
    [ChartPatternAuditReasonCodeEnum.apexTooLate]: 'Setup too late near apex',
    [ChartPatternAuditReasonCodeEnum.badBreakout]: 'Bad confirmation',
    [ChartPatternAuditReasonCodeEnum.stale]: 'Stale pattern',
    [ChartPatternAuditReasonCodeEnum.duplicate]: 'Duplicate',
    [ChartPatternAuditReasonCodeEnum.windowWrong]: 'Wrong time window',
    [ChartPatternAuditReasonCodeEnum.wrongLifecycleState]: 'Wrong status',
    [ChartPatternAuditReasonCodeEnum.other]: 'Other'
};

const genericIssueCodeOptions: ChartPatternAuditReasonCode[] = [
    ChartPatternAuditReasonCodeEnum.badBreakout,
    ChartPatternAuditReasonCodeEnum.stale,
    ChartPatternAuditReasonCodeEnum.duplicate,
    ChartPatternAuditReasonCodeEnum.windowWrong,
    ChartPatternAuditReasonCodeEnum.wrongLifecycleState,
    ChartPatternAuditReasonCodeEnum.other
];

const cupHandleIssueCodeOptions: ChartPatternAuditReasonCode[] = [
    ChartPatternAuditReasonCodeEnum.notRounded,
    ChartPatternAuditReasonCodeEnum.tooVShaped,
    ChartPatternAuditReasonCodeEnum.handleTooDeep,
    ChartPatternAuditReasonCodeEnum.handleTooLong,
    ChartPatternAuditReasonCodeEnum.handleTooLow,
    ChartPatternAuditReasonCodeEnum.badRimSymmetry,
    ...genericIssueCodeOptions
];

const flagIssueCodeOptions: ChartPatternAuditReasonCode[] = [
    ChartPatternAuditReasonCodeEnum.weakImpulseLeg,
    ChartPatternAuditReasonCodeEnum.poleTooLong,
    ChartPatternAuditReasonCodeEnum.flagTooFlat,
    ChartPatternAuditReasonCodeEnum.flagTooDeep,
    ChartPatternAuditReasonCodeEnum.flagTooWide,
    ChartPatternAuditReasonCodeEnum.poorChannelStructure,
    ...genericIssueCodeOptions
];

const triangleIssueCodeOptions: ChartPatternAuditReasonCode[] = [
    ChartPatternAuditReasonCodeEnum.poorTriangleStructure,
    ChartPatternAuditReasonCodeEnum.weakPriorTrend,
    ChartPatternAuditReasonCodeEnum.upperBoundaryWrongSlope,
    ChartPatternAuditReasonCodeEnum.lowerBoundaryWrongSlope,
    ChartPatternAuditReasonCodeEnum.weakBoundaryTouches,
    ChartPatternAuditReasonCodeEnum.poorCompression,
    ChartPatternAuditReasonCodeEnum.poorTrendlineFit,
    ChartPatternAuditReasonCodeEnum.apexTooLate,
    ...genericIssueCodeOptions
];

export const getIssueCodeOptions = (patternKey: ChartPatternKeyType | null | undefined): ChartPatternAuditReasonCode[] => {
    if (patternKey === ChartPatternKey.bullFlag || patternKey === ChartPatternKey.bearFlag) {
        return flagIssueCodeOptions;
    }

    if (patternKey === ChartPatternKey.cupHandle) {
        return cupHandleIssueCodeOptions;
    }

    if (
        patternKey === ChartPatternKey.ascendingTriangle ||
        patternKey === ChartPatternKey.descendingTriangle ||
        patternKey === ChartPatternKey.symmetricalTriangle ||
        patternKey === ChartPatternKey.risingWedge ||
        patternKey === ChartPatternKey.fallingWedge
    ) {
        return triangleIssueCodeOptions;
    }

    return genericIssueCodeOptions;
};

export const renderStatusLabels: Record<ChartPatternAuditRenderStatus, string> = {
    [ChartPatternAuditRenderStatusEnum.pending]: 'Pending',
    [ChartPatternAuditRenderStatusEnum.rendering]: 'Generating…',
    [ChartPatternAuditRenderStatusEnum.rendered]: 'Ready',
    [ChartPatternAuditRenderStatusEnum.failed]: 'Failed'
};

export const chartViewLabels: Record<ChartPatternAuditArtifactType, string> = {
    [ChartPatternAuditArtifactType.rawWide]: 'Raw · Wide',
    [ChartPatternAuditArtifactType.rawFocused]: 'Raw · Focused',
    [ChartPatternAuditArtifactType.annotatedWide]: 'Annotated · Wide',
    [ChartPatternAuditArtifactType.annotatedFocused]: 'Annotated · Focused'
};

export const patternKeyLabels: Record<string, string> = {
    [ChartPatternKey.cupHandle]: 'Cup & Handle',
    [ChartPatternKey.doubleBottom]: 'Double Bottom',
    [ChartPatternKey.doubleTop]: 'Double Top',
    [ChartPatternKey.ascendingTriangle]: 'Ascending Triangle',
    [ChartPatternKey.descendingTriangle]: 'Descending Triangle',
    [ChartPatternKey.symmetricalTriangle]: 'Symmetrical Triangle',
    [ChartPatternKey.bullFlag]: 'Bull Flag',
    [ChartPatternKey.bearFlag]: 'Bear Flag',
    [ChartPatternKey.headShoulders]: 'Head & Shoulders',
    [ChartPatternKey.inverseHeadShoulders]: 'Inverse Head & Shoulders',
    [ChartPatternKey.risingWedge]: 'Rising Wedge',
    [ChartPatternKey.fallingWedge]: 'Falling Wedge'
};

// ── Default values ──

export const defaultBatchName = () => `Pattern Audit ${getDate({ format: 'ymd' })}`;

export const defaultBatchForm: CreateBatchFormState = {
    name: defaultBatchName(),
    patternKey: ChartPatternKey.doubleBottom,
    barDuration: '1d',
    maxItems: '250',
    minQualityScore: '60',
    state: ChartPatternState.formed,
    onlySelectedOccurrences: true,
    notes: ''
};

export const defaultPrepareSessionForm: PrepareSessionFormState = {
    patternKey: ChartPatternKey.doubleBottom,
    barDuration: '1d',
    maxItemsPerBatch: '40',
    includeHistoricalBatches: true,
    historicalLookbackDays: '730',
    historicalMinQualityScore: '60'
};

export const emptyReviewForm: ReviewFormState = {
    verdict: ChartPatternAuditVerdictEnum.correct,
    confidence: '',
    reasonCodes: [],
    notes: ''
};

// ── Helper functions ──

export const formatPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    return `${value.toFixed(2)}%`;
};

export const formatPrice = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(2);
};

export const buildChartAuditLink = (detail: ChartPatternAuditItemDetail | null | undefined): string => {
    if (!detail) return '#';

    const params = new URLSearchParams();
    params.set('variant', 'annotatedWide');
    params.set('mode', 'focused');

    return `/tools/chartPatternAudit/render/${detail.item.id}?${params.toString()}`;
};

export const buildArtifactImageUrl = (artifact: ChartPatternAuditArtifact): string => {
    const queryParams = new URLSearchParams({
        v: `${artifact.createdAt}-${artifact.byteSize ?? 'na'}`
    });

    return `${API_DOMAIN}/api/chartPatternAudit/artifacts/${artifact.id}/image?${queryParams.toString()}`;
};

export const buildAssetChartLink = (detail: ChartPatternAuditItemDetail): string =>
    `/asset/${detail.item.assetId}?chartPatterns=1&chartPatternMode=focused&chartPatternOccurrenceId=${detail.item.occurrenceId}&chartPatternAsOfDateTime=${detail.item.snapshotDateTime}&duration=${detail.item.barDuration}`;

export const confidenceMap: Record<string, number> = {
    low: 0.33,
    medium: 0.66,
    high: 1.0
};

export const confidenceFromNumeric = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    if (value <= 0.5) return 'low';
    if (value <= 0.83) return 'medium';
    return 'high';
};

export const timeframeLabel = (barDuration: string | null | undefined): string => {
    if (!barDuration) return 'All';
    if (barDuration === '5m') return '5 Minute';
    if (barDuration === '15m') return '15 Minute';
    if (barDuration === '30m') return '30 Minute';
    if (barDuration === '240m') return '4 Hour';
    if (barDuration === '180m') return '3 Hour';
    if (barDuration === '120m') return '2 Hour';
    if (barDuration === '60m') return '60 Minute';
    if (barDuration === '1d') return 'Daily';
    if (barDuration === '1w') return 'Weekly';
    return barDuration;
};
